import React, { FC, useState, useEffect, useMemo, useRef } from 'react';
import { Modal } from 'antd';
import { UnavailableDateEntry, UnavailableTimeSlot } from 'src/api/queries/useGetTrainerSchedule';
import { TimeSlotPicker } from 'src/screens/OnboardingTrainer/screens/Schedule/components/TimeSlotPicker';
import {
  timeToMinutes,
  doSlotsOverlap,
} from 'src/screens/OnboardingTrainer/screens/Schedule/utils/timezoneUtils';
import toast from 'src/utils/appAlert';
import './UnavailableDatesModal.scss';

interface UnavailableDatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialDates: UnavailableDateEntry[];
  onSave: (dates: UnavailableDateEntry[]) => Promise<void>;
  isSaving?: boolean;
  focusedDate?: string | null;
}

const modalStyles = {
  mask: {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(4, 5, 27, 0.8)',
  },
  container: {
    background: '#0c0f2b',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '20px',
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.6)',
    padding: 0,
    color: '#ffffff',
    overflow: 'hidden',
  },
  body: {
    padding: 0,
    background: 'transparent',
  },
};

const WEEKDAY_NAMES = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export const UnavailableDatesModal: FC<UnavailableDatesModalProps> = ({
  isOpen,
  onClose,
  initialDates,
  onSave,
  isSaving = false,
  focusedDate,
}) => {
  // Working copy of unavailable dates
  const [dates, setDates] = useState<UnavailableDateEntry[]>([]);
  const [activeDateStr, setActiveDateStr] = useState<string | null>(null);

  // Month navigation: year and month (0-indexed)
  const [currentMonth, setCurrentMonth] = useState<Date>(() => new Date());

  const datesListRef = useRef<HTMLDivElement>(null);

  // Reset or initialize when modal opens
  useEffect(() => {
    if (isOpen) {
      const copy: UnavailableDateEntry[] = JSON.parse(JSON.stringify(initialDates || []));
      setDates(copy);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Determine initial calendar month and active date
      let targetDate = focusedDate;
      if (!targetDate && copy.length > 0) {
        // Pick first non-past date if available
        const upcoming = copy.find((d) => {
          const [y, m, day] = d.date.split('-').map(Number);
          const dt = new Date(y, m - 1, day);
          dt.setHours(0, 0, 0, 0);
          return dt.getTime() >= today.getTime();
        });
        targetDate = upcoming?.date;
      }

      if (targetDate) {
        const [y, m, day] = targetDate.split('-').map(Number);
        const dt = new Date(y, m - 1, day);
        dt.setHours(0, 0, 0, 0);
        if (dt.getTime() >= today.getTime()) {
          setActiveDateStr(targetDate);
        } else {
          setActiveDateStr(null);
        }
        if (y > today.getFullYear() || (y === today.getFullYear() && m - 1 >= today.getMonth())) {
          setCurrentMonth(new Date(y, m - 1, 1));
        } else {
          setCurrentMonth(new Date());
        }
      } else {
        setActiveDateStr(null);
        setCurrentMonth(new Date());
      }
    }
  }, [isOpen, initialDates, focusedDate]);

  // Calendar calculations
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth(); // 0-indexed

  const monthLabel = currentMonth.toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric',
  });

  const isPrevMonthDisabled = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonthNum = today.getMonth();
    return year < currentYear || (year === currentYear && month <= currentMonthNum);
  }, [year, month]);

  const handlePrevMonth = () => {
    if (isPrevMonthDisabled) return;
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  // Calendar cells generation (Monday-first)
  const calendarCells = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1);
    // getDay() gives 0 for Sunday, 1 for Monday... We want Monday = 0, Sunday = 6
    const firstDayIndex = (firstDayOfMonth.getDay() + 6) % 7;

    const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const cells: Array<{
      dateStr: string;
      dayNumber: number;
      isCurrentMonth: boolean;
      isPast: boolean;
      isToday: boolean;
      hasUnavailableSlots: boolean;
      isSelected: boolean;
    }> = [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Prev month padding
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = daysInPrevMonth - i;
      const prevDate = new Date(year, month - 1, dayNum);
      prevDate.setHours(0, 0, 0, 0);
      const dateStr = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      cells.push({
        dateStr,
        dayNumber: dayNum,
        isCurrentMonth: false,
        isPast: prevDate.getTime() < today.getTime(),
        isToday: false,
        hasUnavailableSlots: dates.some((d) => d.date === dateStr),
        isSelected: activeDateStr === dateStr,
      });
    }

    // Current month days
    for (let dayNum = 1; dayNum <= daysInCurrentMonth; dayNum++) {
      const thisDate = new Date(year, month, dayNum);
      thisDate.setHours(0, 0, 0, 0);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      cells.push({
        dateStr,
        dayNumber: dayNum,
        isCurrentMonth: true,
        isPast: thisDate.getTime() < today.getTime(),
        isToday: thisDate.getTime() === today.getTime(),
        hasUnavailableSlots: dates.some((d) => d.date === dateStr),
        isSelected: activeDateStr === dateStr,
      });
    }

    // Next month padding to fill grid to multiple of 7 (at least 35 or 42 cells)
    const remaining = (7 - (cells.length % 7)) % 7;
    for (let dayNum = 1; dayNum <= remaining; dayNum++) {
      const nextDate = new Date(year, month + 1, dayNum);
      nextDate.setHours(0, 0, 0, 0);
      const dateStr = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      cells.push({
        dateStr,
        dayNumber: dayNum,
        isCurrentMonth: false,
        isPast: nextDate.getTime() < today.getTime(),
        isToday: false,
        hasUnavailableSlots: dates.some((d) => d.date === dateStr),
        isSelected: activeDateStr === dateStr,
      });
    }

    return cells;
  }, [year, month, dates, activeDateStr]);

  // Click on a calendar day
  const handleSelectDay = (dateStr: string, isPast: boolean) => {
    if (isPast) return;
    setActiveDateStr(dateStr);

    const existingIdx = dates.findIndex((d) => d.date === dateStr);
    if (existingIdx === -1) {
      // Add new unavailable date with a default slot
      const newEntry: UnavailableDateEntry = {
        date: dateStr,
        allDay: false,
        slots: [
          {
            id: `slot-${dateStr}-1`,
            start: '12:00',
            end: '13:30',
          },
        ],
      };
      setDates((prev) => {
        const next = [...prev, newEntry];
        return next.sort((a, b) => a.date.localeCompare(b.date));
      });
    }

    // Scroll right list to date
    setTimeout(() => {
      const el = document.getElementById(`date-entry-${dateStr}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  // Remove an entire date
  const handleRemoveDate = (dateStr: string) => {
    setDates((prev) => prev.filter((d) => d.date !== dateStr));
    if (activeDateStr === dateStr) {
      const remaining = dates.filter((d) => d.date !== dateStr);
      setActiveDateStr(remaining[0]?.date || null);
    }
  };

  // Toggle all day for a date
  const handleToggleAllDay = (dateStr: string) => {
    setDates((prev) =>
      prev.map((d) => {
        if (d.date !== dateStr) return d;
        const newAllDay = !d.allDay;
        return {
          ...d,
          allDay: newAllDay,
          slots: newAllDay
            ? [{ id: `slot-${dateStr}-allday`, start: '00:00', end: '24:00' }]
            : [{ id: `slot-${dateStr}-1`, start: '12:00', end: '13:30' }],
        };
      }),
    );
  };

  // Change slot time
  const handleSlotTimeChange = (
    dateStr: string,
    slotId: string,
    field: 'start' | 'end',
    val: string,
  ) => {
    setDates((prev) =>
      prev.map((d) => {
        if (d.date !== dateStr) return d;
        return {
          ...d,
          slots: d.slots.map((s) => (s.id === slotId ? { ...s, [field]: val } : s)),
        };
      }),
    );
  };

  // Add time range to a date
  const handleAddSlot = (dateStr: string) => {
    setDates((prev) =>
      prev.map((d) => {
        if (d.date !== dateStr) return d;
        if (d.slots.length >= 5) {
          toast.error('Maximum of 5 time ranges per date.');
          return d;
        }

        // Smart next slot progression based on last slot end time
        const lastSlot = d.slots[d.slots.length - 1];
        let nextStart = '14:00';
        let nextEnd = '17:00';

        if (lastSlot) {
          const lastEndMins = timeToMinutes(lastSlot.end);
          if (lastEndMins + 60 <= 1440) {
            const startH = Math.floor(lastEndMins / 60);
            const startM = lastEndMins % 60;
            nextStart = `${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
            const endMins = Math.min(lastEndMins + 90, 1440);
            const endH = Math.floor(endMins / 60);
            const endM = endMins % 60;
            nextEnd = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
          }
        }

        const newSlot: UnavailableTimeSlot = {
          id: `slot-${dateStr}-${Date.now()}`,
          start: nextStart,
          end: nextEnd,
        };

        return {
          ...d,
          slots: [...d.slots, newSlot],
        };
      }),
    );
  };

  // Remove slot from a date
  const handleRemoveSlot = (dateStr: string, slotId: string) => {
    setDates((prev) =>
      prev.map((d) => {
        if (d.date !== dateStr) return d;
        const updatedSlots = d.slots.filter((s) => s.id !== slotId);
        // If no slots left, remove date altogether or leave default
        if (updatedSlots.length === 0) {
          return {
            ...d,
            allDay: true,
            slots: [{ id: `slot-${dateStr}-allday`, start: '00:00', end: '24:00' }],
          };
        }
        return {
          ...d,
          slots: updatedSlots,
        };
      }),
    );
  };

  // Validation per date
  const getDateErrors = (entry: UnavailableDateEntry): string | null => {
    if (entry.allDay) return null;
    if (entry.slots.length === 0) return 'At least one time range is required';

    for (const slot of entry.slots) {
      const startMins = timeToMinutes(slot.start);
      const endMins = timeToMinutes(slot.end);
      if (endMins <= startMins) {
        return 'End time must be after start time';
      }
    }

    // Check overlap
    for (let i = 0; i < entry.slots.length; i++) {
      for (let j = i + 1; j < entry.slots.length; j++) {
        if (
          doSlotsOverlap(
            entry.slots[i].start,
            entry.slots[i].end,
            entry.slots[j].start,
            entry.slots[j].end,
          )
        ) {
          return 'Time ranges cannot overlap';
        }
      }
    }

    return null;
  };

  // Global validation check
  const hasAnyErrors = useMemo(() => {
    return dates.some((d) => getDateErrors(d) !== null);
  }, [dates]);

  // Save handler
  const handleSave = async () => {
    if (hasAnyErrors || isSaving) return;

    try {
      // Sort slots chronologically within each date
      const cleanedDates = dates.map((d) => ({
        ...d,
        slots: d.slots.slice().sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start)),
      }));

      await onSave(cleanedDates);
      toast.success('Unavailable dates saved successfully.');
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save dates';
      toast.error(msg);
    }
  };

  const hasDates = dates.length > 0;

  // Format date helper for side list
  const formatHeading = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    return dateObj.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Modal
      open={isOpen}
      footer={null}
      onCancel={onClose}
      centered
      width={hasDates ? 880 : 520}
      closeIcon={null}
      rootClassName="unavailable-dates-modal-root"
      className="unavailable-dates-modal"
      styles={modalStyles}
    >
      <div className={`unavailable-modal-inner ${hasDates ? 'has-side-panel' : ''}`}>
        {/* Header */}
        <div className="modal-header-row">
          <div className="header-titles">
            <h2 className="modal-title">{'Select your unavailable dates'}</h2>
            <p className="modal-subtitle">
              {"Times outside your available hours won't affect your schedule."}
            </p>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content Layout: Calendar (Left) & Dates List (Right when dates exist) */}
        <div className="modal-body-layout">
          {/* Calendar Widget */}
          <div className="calendar-widget-col">
            {/* Month Navigator */}
            <div className="calendar-month-nav">
              <span className="month-label">{monthLabel}</span>
              <div className="nav-arrows">
                <button
                  type="button"
                  className="nav-arrow-btn"
                  onClick={handlePrevMonth}
                  disabled={isPrevMonthDisabled}
                  aria-label="Previous month"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="nav-arrow-btn"
                  onClick={handleNextMonth}
                  aria-label="Next month"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Weekday Labels */}
            <div className="calendar-weekdays-row">
              {WEEKDAY_NAMES.map((name) => (
                <span key={name} className="weekday-col-header">
                  {name}
                </span>
              ))}
            </div>

            {/* Calendar Days Grid */}
            <div className="calendar-days-grid">
              {calendarCells.map((cell) => {
                const cellClasses = [
                  'calendar-day-cell',
                  cell.isCurrentMonth ? 'current-month' : 'other-month',
                  cell.isToday ? 'is-today' : '',
                  cell.hasUnavailableSlots ? 'has-slots' : '',
                  cell.isSelected ? 'selected-day' : '',
                  cell.isPast ? 'is-past' : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <button
                    key={cell.dateStr}
                    type="button"
                    className={cellClasses}
                    disabled={cell.isPast}
                    onClick={() => handleSelectDay(cell.dateStr, cell.isPast)}
                  >
                    <span className="day-number-text">{cell.dayNumber}</span>
                    {cell.hasUnavailableSlots && <span className="day-slot-dot" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Side Panel: Selected Dates & Times */}
          {hasDates && (
            <div className="dates-side-panel-col">
              <div className="side-panel-header">
                <span className="side-panel-title">{'Selected Dates'}</span>
                <span className="side-panel-count">{`${dates.length} date${dates.length > 1 ? 's' : ''}`}</span>
              </div>

              <div className="side-panel-scroll-list" ref={datesListRef}>
                {dates.map((entry) => {
                  const error = getDateErrors(entry);
                  const isFocused = activeDateStr === entry.date;

                  return (
                    <div
                      key={entry.date}
                      id={`date-entry-${entry.date}`}
                      className={`date-entry-card ${isFocused ? 'focused' : ''}`}
                    >
                      {/* Date Top Row */}
                      <div className="entry-card-header">
                        <span className="date-title-text">{formatHeading(entry.date)}</span>
                        <div className="entry-header-actions">
                          <button
                            type="button"
                            className={`btn-toggle-allday ${entry.allDay ? 'active' : ''}`}
                            onClick={() => handleToggleAllDay(entry.date)}
                          >
                            {entry.allDay ? 'All day' : 'Custom'}
                          </button>
                          <button
                            type="button"
                            className="btn-remove-date"
                            onClick={() => handleRemoveDate(entry.date)}
                            aria-label="Remove date"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Time Slots */}
                      {entry.allDay ? (
                        <div className="allday-display-row">
                          <span className="allday-label">{'00:00 - 24:00 (All day)'}</span>
                        </div>
                      ) : (
                        <div className="slots-list">
                          {entry.slots.map((slot) => (
                            <div key={slot.id} className="time-slot-row">
                              <TimeSlotPicker
                                value={slot.start}
                                type="start"
                                onChange={(val) =>
                                  handleSlotTimeChange(entry.date, slot.id, 'start', val)
                                }
                              />
                              <span className="slot-divider">{'-'}</span>
                              <TimeSlotPicker
                                value={slot.end}
                                type="end"
                                minTime={slot.start}
                                onChange={(val) =>
                                  handleSlotTimeChange(entry.date, slot.id, 'end', val)
                                }
                              />
                              {entry.slots.length > 1 && (
                                <button
                                  type="button"
                                  className="btn-remove-slot"
                                  onClick={() => handleRemoveSlot(entry.date, slot.id)}
                                  aria-label="Remove time range"
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}

                          {entry.slots.length < 5 && (
                            <button
                              type="button"
                              className="btn-add-slot"
                              onClick={() => handleAddSlot(entry.date)}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                              <span>{'Add time range'}</span>
                            </button>
                          )}
                        </div>
                      )}

                      {/* Error feedback */}
                      {error && <span className="entry-error-msg">{error}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-actions-footer">
          <button type="button" className="btn-footer-cancel" onClick={onClose} disabled={isSaving}>
            {'Cancel'}
          </button>
          <button
            type="button"
            className="btn-footer-save"
            onClick={handleSave}
            disabled={hasAnyErrors || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Dates'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
